import React from "react";
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
} from "@mui/material";
import { buildSignaturesTemplate, getDefaultRoleLabels } from "../utils/signatureUtils";

const noSelectStyle = {
  WebkitTouchCallout: "none",
  WebkitUserSelect: "none",
  KhtmlUserSelect: "none",
  MozUserSelect: "none",
  msUserSelect: "none",
  userSelect: "none",
};

const defaultRoleLabels = getDefaultRoleLabels();

export const INITIALS_ROLES = [
  { key: "client.buy", label: "Buyer" },
  { key: "client.sell", label: "Seller" },
  { key: "team.buy", label: "Buyer Agent" },
  { key: "team.sell", label: "Seller Agent" },
  { key: "other.managingBroker", label: "Managing Broker" },
  { key: "other.propertyManager", label: "Property Manager" },
];

/** Rows on an initialsJSON role, tolerating the pre-migration `signatures` key. */
export const initialsRowsOf = (roleValue) => roleValue?.initials ?? roleValue?.signatures ?? [];

/**
 * Which roles INITIAL this form.
 *
 * Deliberately unlike Signer Roles: ticking a role here creates NO slots. A slot exists only once
 * an initials tab is placed on the PDF, so the form can never carry an initials slot with nowhere
 * to initial. (Signer Roles seeds a full set of slots and relies on prune-on-save to trim the ones
 * left untabbed — that stays as it is.)
 *
 * max / additionalSignaturePage / default are computed from the placed tabs at publish
 * (generateInitialsJSON), so there is nothing to edit here. Role metadata that IS author-owned —
 * min, altSigners — is edited in JSON.
 *
 * @param {Object}   initials        the initialsJSON map (role metadata + rows), or null.
 * @param {Array}    annotations     placed initialsAnnotations — initials tabs on the PDF.
 * @param {Array}    bindings        initialerAttributeBindings — fields placed inside the initials
 *   block. These can exist with no tab, so unticking must account for them too or they are
 *   orphaned silently.
 * @param {Function} onInitialsRolesChange called with the next initials map.
 * @param {Function} onBlocked       called with a message when unticking is refused.
 */
const InitialsRoleSelector = ({
  initials,
  annotations = [],
  bindings = [],
  onInitialsRolesChange,
  onBlocked,
}) => {
  const template = buildSignaturesTemplate();
  const current = initials && typeof initials === "object" ? initials : {};

  const placedCount = (roleKey) => annotations.filter((a) => a?.roleKey === roleKey).length;
  const boundCount = (roleKey) => bindings.filter((b) => b?.roleKey === roleKey).length;

  const handleChange = (roleKey, label) => {
    if (current[roleKey]) {
      const placed = placedCount(roleKey);
      const bound = boundCount(roleKey);
      if (placed > 0 || bound > 0) {
        // Unticking would orphan real placed content. Refuse and say what is in the way, rather
        // than deleting something the user cannot see from this card.
        const parts = [];
        if (placed > 0) parts.push(`${placed} initials tab${placed === 1 ? "" : "s"}`);
        if (bound > 0) parts.push(`${bound} field${bound === 1 ? "" : "s"}`);
        onBlocked?.(
          `${label} still has ${parts.join(" and ")} placed on the PDF. ` +
            `Remove ${placed + bound === 1 ? "it" : "them"} first, then untick the role.`,
        );
        return;
      }
      const { [roleKey]: _removed, ...rest } = current;
      onInitialsRolesChange(rest);
      return;
    }
    // Metadata only — no rows. The role becomes selectable for placing tabs; it publishes
    // nothing until one exists.
    const t = template[roleKey] || {};
    onInitialsRolesChange({
      ...current,
      [roleKey]: {
        integrationRole: t.integrationRole,
        meviDealRole: roleKey,
        min: t.min ?? 1,
        max: 0,
        default: 0,
        additionalSignaturePage: 1,
        altSigners: [],
        initials: [],
      },
    });
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Initials Roles"
        subheader="Select the roles that will be INITIALLING this form. Unlike Signer Roles, ticking a role adds no slots — a slot appears as you place each initials tab on the PDF. Counts and min/altSigners are set from the placed tabs or in JSON."
        sx={noSelectStyle}
      />
      <Divider />
      <CardContent sx={noSelectStyle}>
        <FormGroup row>
          {INITIALS_ROLES.map((role) => {
            const placed = placedCount(role.key);
            const bound = boundCount(role.key);
            const effectiveLabel = defaultRoleLabels[role.key] ?? role.label;
            return (
              <FormControlLabel
                key={role.key}
                control={
                  <Checkbox
                    checked={!!current[role.key]}
                    onChange={() => handleChange(role.key, effectiveLabel)}
                    sx={{ "&.Mui-checked": { color: "#00a896" } }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                    <span>{effectiveLabel}</span>
                    {(placed > 0 || bound > 0) && (
                      <Typography variant="caption" sx={{ color: "#00a896", fontWeight: 600 }}>
                        {[
                          placed > 0 ? `${placed} tab${placed === 1 ? "" : "s"}` : null,
                          bound > 0 ? `${bound} field${bound === 1 ? "" : "s"}` : null,
                        ]
                          .filter(Boolean)
                          .join(", ")}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ mr: 4 }}
              />
            );
          })}
        </FormGroup>
      </CardContent>
    </Card>
  );
};

export default InitialsRoleSelector;
